import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";

import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { requestSignupOtp, verifySignupOtp } from "@/lib/signup-otp.functions";

function RegisterPage() {
  const navigate = useNavigate();
  const requestOtp = useServerFn(requestSignupOtp);
  const verifyOtp = useServerFn(verifySignupOtp);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"form" | "otp">("form");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      if (step === "form") {
        await requestOtp({ data: { email } });
        setStep("otp");
        return;
      }
      if (!/^\d{6}$/.test(otp.trim())) {
        setError("enter the 6-digit code");
        return;
      }
      await verifyOtp({ data: { email, code: otp.trim(), password } });
      const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
      if (signInErr) throw signInErr;
      navigate({ to: "/onboarding" });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "could not create account");
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    setError(null);
    setBusy(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}/onboarding`,
        extraParams: { prompt: "select_account" },
      });
      if (result.error) throw result.error;
      if (result.redirected) return;
      navigate({ to: "/onboarding" });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "google sign-in failed");
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16 bg-gradient-to-br from-[hsl(190_55%_94%)] via-white to-[hsl(190_45%_92%)]">
      <Card className="w-full max-w-md p-8" style={{ fontFamily: "Poppins, sans-serif" }}>
        <div className="mb-6">
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">← back to site</Link>
          <h1 className="mt-4 text-2xl font-bold">create your account</h1>
          <p className="mt-1 text-sm text-muted-foreground">save places, follow what's near you</p>
        </div>

        <Button type="button" variant="outline" onClick={handleGoogle} disabled={busy} className="w-full mb-4 gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
          </svg>
          sign up with google
        </Button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">or email</span>
          </div>
        </div>

        <form onSubmit={handleSignUp} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" disabled={step === "otp"} />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">password</Label>
              <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground underline">forgot password?</Link>
            </div>
            <div className="relative">
              <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" className="pr-10" disabled={step === "otp"} />
              <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={showPassword ? "hide password" : "show password"} tabIndex={-1}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">at least 8 characters</p>
          </div>
          {step === "otp" && (
            <div className="space-y-2">
              <Label htmlFor="otp">verification code</Label>
              <Input id="otp" inputMode="numeric" autoComplete="one-time-code" value={otp} onChange={(e) => setOtp(e.target.value)} required maxLength={6} />
              <p className="text-xs text-muted-foreground">enter the 6-digit code we emailed you</p>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button type="submit" disabled={busy} className="w-full">
            {busy ? (step === "otp" ? "verifying..." : "sending code...") : step === "otp" ? "verify and create account" : "send verification code"}
          </Button>
          {step === "otp" && (
            <button
              type="button"
              className="w-full text-xs text-muted-foreground underline"
              onClick={() => { setStep("form"); setOtp(""); setError(null); }}
            >
              use a different email
            </button>
          )}
        </form>

        <p className="mt-6 text-sm text-center text-muted-foreground">
          already registered? <Link to="/login" className="underline hover:text-foreground">sign in</Link>
        </p>
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/register")({
  head: () => ({
    meta: [
      { title: "Create account — what Finland" },
      { name: "description", content: "Create your what Finland account." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: RegisterPage,
});
